// lib/footfall/scenario.ts
import type { BrandCategory, PulseLayoutChange, PulseMove, PulsePlan, PulseScenario, TableDoc, TableSheet } from '#types/types';
import { childrenOf, parentsOf, validLayout } from './layout';
import { normalise, ordinal, type PulsePlace } from './pulse';

export const REFERENCE_RENT_MAX = 5000;
export const YIELD_MIN = 2;
export const YIELD_MAX = 15;
export const RENT_FREE_MAX = 36;
export const MOVES_MAX = 300;
export const businessPlanSheetId = (propertyId: string) => `asset:${propertyId}:pulse-business-plan`;
export const BUSINESS_PLAN_NAME = 'Business Plan';

export const blankScenario = (): PulseScenario => ({ revision: '', savedAt: '', referenceRent: null, yieldPct: null, levelId: null, layer: 'mix', moves: [] });
/** Potential rent, €/m²/year: the reference rent scaled by position, from 0.7× at the quietest shop to 1.3× at the busiest. */
export const potentialRent = (referenceRent: number, percentile: number) => Math.round(referenceRent * (0.7 + 0.6 * percentile / 100));

// ---------- rent roll ----------
export interface RentRollRow { unit: string; tenant: string; areaSqm: number | null; annualRent: number | null; leaseEnd: string | null }

const cellsOf = (sheet: TableSheet | undefined) => sheet?.cells ?? {};
function rowsOf(sheet: TableSheet | undefined): Array<Record<string, string>> {
  const cells = cellsOf(sheet);
  const headers: string[] = [];
  let rows = 0;
  for (const [key, value] of Object.entries(cells)) {
    const [r, c] = key.split(',').map(Number) as [number, number];
    if (r === 0) headers[c] = value.trim();
    rows = Math.max(rows, r + 1);
  }
  return Array.from({ length: Math.max(0, rows - 1) }, (_, i) => Object.fromEntries(headers.flatMap((h, c) => h ? [[h, (cells[`${i + 1},${c}`] ?? '').trim()]] : [])));
}
const amount = (value: string | undefined) => { const n = Number(String(value ?? '').replace(/[\s\u00a0€]/g, '').replace(',', '.')); return value && Number.isFinite(n) ? n : null; };
const isoDate = (value: string | undefined) => value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;

/** The asset's Rent Roll and Lease Dates sheets as one row per tenancy; a zero or blank rent is unknown, never 0. */
export function rentRoll(workbook: TableDoc | null | undefined): RentRollRow[] {
  if (!workbook) return [];
  const leaseEnds = new Map(rowsOf(workbook.sheets.find(s => s.name === 'Lease Dates')).map(r => [`${r['Unit']}|${r['Tenant']}`, isoDate(r['Lease expiry'])]));
  return rowsOf(workbook.sheets.find(s => s.name === 'Rent Roll')).filter(r => r['Tenant']).map(r => {
    const rent = amount(r['Contractual rent']);
    const basis = (r['Rent basis'] ?? '').toUpperCase();
    const annual = rent === null || rent <= 0 ? null : basis === 'MONTHLY' ? rent * 12 : basis === '' || basis === 'YEARLY' || basis === 'ANNUAL' ? rent : null;
    return { unit: r['Unit'] ?? '', tenant: r['Tenant']!, areaSqm: amount(r['Area (m²)']), annualRent: annual, leaseEnd: isoDate(r['Lease expiry']) ?? leaseEnds.get(`${r['Unit']}|${r['Tenant']}`) ?? null };
  });
}
export function rentRowFor(tenant: string | null, rows: RentRollRow[]): RentRollRow | null {
  if (!tenant) return null;
  const name = normalise(tenant);
  return rows.find(r => normalise(r.tenant) === name) ?? null;
}

/** A shop's rent-roll row, by its brand's name first, then by the centre's own label. */
export const rentRowForPlace = (place: Pick<PulsePlace, 'name' | 'feature'>, rows: RentRollRow[]) => rentRowFor(place.name, rows) ?? rentRowFor(place.feature.name, rows);

// ---------- validation ----------
const today = () => new Date().toISOString().slice(0, 10);
export function referenceRentError(value: number | null): string | null {
  return value === null ? null : !Number.isFinite(value) || value < 1 || value > REFERENCE_RENT_MAX ? `Reference rent must be between 1 and ${REFERENCE_RENT_MAX.toLocaleString('en-GB')} €/m²/year.` : null;
}
export function yieldError(value: number | null): string | null {
  return value === null ? null : !Number.isFinite(value) || value < YIELD_MIN || value > YIELD_MAX ? `Yield must be between ${YIELD_MIN} % and ${YIELD_MAX} %.` : null;
}
/** The first problem with a move a user is entering, in plain words; null when it can be kept. */
export function moveError(move: Pick<PulseMove, 'rentPerSqm' | 'rentFreeMonths' | 'works' | 'start'>, now = today(), existingStart?: string): string | null {
  if (move.rentPerSqm !== null && (!Number.isFinite(move.rentPerSqm) || move.rentPerSqm <= 0 || move.rentPerSqm > REFERENCE_RENT_MAX * 3)) return `Target rent must be a positive number of €/m²/year, up to ${(REFERENCE_RENT_MAX * 3).toLocaleString('en-GB')}.`;
  if (!Number.isInteger(move.rentFreeMonths) || move.rentFreeMonths < 0 || move.rentFreeMonths > RENT_FREE_MAX) return `Rent-free months must be a whole number from 0 to ${RENT_FREE_MAX}.`;
  if (!Number.isFinite(move.works) || move.works < 0) return 'Works must be zero or a positive amount in €.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(move.start)) return 'Start date is not a date.';
  if (move.start < now && move.start !== existingStart) return 'Start date cannot be in the past.';
  return null;
}
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown, max: number) => typeof v === 'string' && v.length <= max;
const num = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
export function validPulseScenario(value: unknown): value is PulseScenario {
  if (!object(value) || !str(value.revision, 100) || !str(value.savedAt, 40)) return false;
  if (!(value.referenceRent === null || (num(value.referenceRent) && !referenceRentError(value.referenceRent as number)))) return false;
  if (!(value.yieldPct === null || (num(value.yieldPct) && !yieldError(value.yieldPct as number)))) return false;
  if (!(value.levelId === null || str(value.levelId, 100)) || !['mix', 'occupancy', 'traffic', 'reviews'].includes(value.layer as string)) return false;
  if (!Array.isArray(value.moves) || value.moves.length > MOVES_MAX || !validLayout(value.layout)) return false;
  const shops = new Set<string>();
  return value.moves.every(m => {
    if (!object(m) || !str(m.shopId, 100) || !m.shopId || shops.has(m.shopId as string) || !['relet', 'vacate'].includes(m.type as string)) return false;
    shops.add(m.shopId as string);
    return (m.brandId === null || str(m.brandId, 100)) && (m.brandName === null || str(m.brandName, 200)) && (m.brandCategory === null || str(m.brandCategory, 60))
      && (m.rentPerSqm === null || num(m.rentPerSqm)) && num(m.rentFreeMonths) && num(m.works) && str(m.start, 10)
      && moveError(m as unknown as PulseMove, '0000-00-00') === null;
  });
}
export const PLAN_NAME_MAX = 80;
export const SCENARIOS_MAX = 3;
export const THESIS_MAX = 4000;
const validVariants = (p: Record<string, unknown>) => {
  if (p.scenarios === undefined && p.activeScenarioId === undefined) return true;
  if (!Array.isArray(p.scenarios) || !p.scenarios.length || p.scenarios.length > SCENARIOS_MAX || !str(p.activeScenarioId, 100)) return false;
  const ids = new Set<string>();
  const ok = p.scenarios.every(v => {
    if (!object(v) || !str(v.id, 100) || !v.id || ids.has(v.id as string) || typeof v.name !== 'string' || !v.name.trim() || v.name.length > 60 || !str(v.thesis, THESIS_MAX)
      || (v.campaignId !== undefined && v.campaignId !== null && !str(v.campaignId, 100))) return false;
    ids.add(v.id as string);
    return validPulseScenario({ ...blankScenario(), moves: v.moves, layout: v.layout });
  });
  return ok && ids.has(p.activeScenarioId as string);
};
export function validPulsePlans(value: unknown): value is Record<string, PulsePlan> {
  if (!object(value) || Object.keys(value).length > 50) return false;
  const plan = ([key, p]: [string, unknown]) => /^[\w-]{1,100}$/.test(key) && validPulseScenario(p) && object(p) && p.id === key
    && typeof p.name === 'string' && p.name.trim().length > 0 && p.name.length <= PLAN_NAME_MAX && str(p.propertyId, 100) && !!p.propertyId && str(p.createdAt, 40) && (p.campaignId === undefined || p.campaignId === null || str(p.campaignId, 100)) && validVariants(p);
  try { return JSON.stringify(value).length <= 400_000 && Object.entries(value).every(plan); } catch { return false; }
}

// ---------- pricing ----------
export interface PricedMove {
  move: PulseMove;
  place: PulsePlace | null;
  areaSqm: number;
  areaBasis: 'rent roll' | 'survey';
  currentRent: number | null;
  targetRent: number | null;
  uplift: number | null;
  rentFreeCost: number | null;
  /** Why the move is out of the totals; null when priced. */
  unpriced: string | null;
  orphaned: boolean;
  warnings: string[];
}
export interface ScenarioTotals {
  moves: number; priced: number; unpriced: number; orphaned: number;
  currentRent: number; targetRent: number; uplift: number; works: number; rentFreeCost: number;
  /** Σ uplift ÷ yield − works − rent-free cost; null until the yield is set. */
  valueCreated: number | null;
  occupancyToday: { let: number; total: number };
  occupancyScenario: { let: number; total: number };
  areaFallbacks: number;
}

/** A reshaping priced: the rent its parent units pay today, which the scenario gives up to re-let the new units. */
export interface PricedLayout { change: PulseLayoutChange; lostRent: number | null; unpriced: string | null }

/**
 * Prices a scenario. `places` are the centre as the scenario reshapes it (its moves apply to them); `today` is the
 * centre as it stands, which the reshaped units' lost rent and today's occupancy are read from.
 */
export function priceScenario(scenario: PulseScenario, places: PulsePlace[], roll: RentRollRow[], shortlistIds: Set<string> | null, now = today(), todayPlaces: PulsePlace[] = places): { rows: PricedMove[]; layoutRows: PricedLayout[]; totals: ScenarioTotals } {
  const byId = new Map(places.map(p => [p.feature.id, p]));
  const todayById = new Map(todayPlaces.map(p => [p.feature.id, p]));
  const layoutRows = (scenario.layout ?? []).filter(c => childrenOf(c).every(id => byId.has(id))).map((change): PricedLayout => {
    const rents = parentsOf(change).map(id => { const p = todayById.get(id); return !p ? null : p.category === 'VACANT' ? 0 : rentRowForPlace(p, roll)?.annualRent ?? null; });
    const missing = rents.some(r => r === null);
    return { change, lostRent: missing ? null : rents.reduce<number>((s, r) => s + r!, 0), unpriced: missing ? 'Current rent of the units it replaces not recorded in the rent roll.' : null };
  });
  const brandUses = new Map<string, number>();
  for (const m of scenario.moves) if (m.brandId) brandUses.set(m.brandId, (brandUses.get(m.brandId) ?? 0) + 1);
  const rows = scenario.moves.map((move): PricedMove => {
    const place = byId.get(move.shopId) ?? null;
    const orphaned = !place || (move.type === 'relet' && !!move.brandId && !!shortlistIds && !shortlistIds.has(move.brandId));
    const roll1 = place ? rentRowForPlace(place, roll) : null;
    const areaSqm = roll1?.areaSqm ?? place?.feature.areaSqm ?? 0;
    const areaBasis = roll1?.areaSqm ? 'rent roll' as const : 'survey' as const;
    const vacant = place?.category === 'VACANT';
    const currentRent = vacant ? 0 : roll1?.annualRent ?? null;
    const targetRent = move.type === 'vacate' ? 0 : move.rentPerSqm === null ? null : Math.round(move.rentPerSqm * areaSqm);
    const warnings: string[] = [];
    if (move.start < now) warnings.push('Start date passed.');
    if (roll1?.leaseEnd && move.type === 'relet' && move.start < roll1.leaseEnd) warnings.push(`Starts before the current lease ends (${roll1.leaseEnd}).`);
    if (move.brandId && (brandUses.get(move.brandId) ?? 0) > 1) warnings.push('This brand is on another move too.');
    if ((move.brandId && move.brandId === place?.brandId) || (move.brandName && place?.name && normalise(move.brandName) === normalise(place.name))) warnings.push('This brand is already the tenant here.');
    const unpriced = orphaned ? (place ? 'The brand left the shortlist.' : 'The shop no longer exists on the plan.')
      : targetRent === null ? 'Set a target rent.' : currentRent === null ? 'Current rent not recorded in the rent roll.' : null;
    const uplift = unpriced ? null : targetRent! - currentRent!;
    const rentFreeCost = unpriced || targetRent === null ? null : Math.round(move.rentFreeMonths * targetRent / 12);
    return { move, place, areaSqm, areaBasis, currentRent, targetRent, uplift, rentFreeCost, unpriced, orphaned, warnings };
  });
  const priced = rows.filter(r => !r.unpriced);
  const sum = (f: (r: PricedMove) => number) => priced.reduce((s, r) => s + f(r), 0);
  const pricedLayout = layoutRows.filter(r => !r.unpriced);
  const lostRent = pricedLayout.reduce((s, r) => s + r.lostRent!, 0);
  const uplift = sum(r => r.uplift!) - lostRent, works = sum(r => r.move.works) + layoutRows.reduce((s, r) => s + r.change.works, 0), rentFreeCost = sum(r => r.rentFreeCost!);
  const isLet = (p: PulsePlace) => p.category !== 'VACANT';
  const after = new Map(places.map(p => [p.feature.id, isLet(p)]));
  for (const r of rows) if (r.place && !r.orphaned) after.set(r.place.feature.id, r.move.type === 'relet');
  return {
    rows,
    layoutRows,
    totals: {
      moves: rows.length, priced: priced.length, unpriced: rows.filter(r => r.unpriced && !r.orphaned).length + layoutRows.length - pricedLayout.length, orphaned: rows.filter(r => r.orphaned).length,
      currentRent: sum(r => r.currentRent!) + lostRent, targetRent: sum(r => r.targetRent!), uplift, works, rentFreeCost,
      valueCreated: scenario.yieldPct ? Math.round(uplift / (scenario.yieldPct / 100) - works - rentFreeCost) : null,
      occupancyToday: { let: todayPlaces.filter(isLet).length, total: todayPlaces.length },
      occupancyScenario: { let: [...after.values()].filter(Boolean).length, total: places.length },
      areaFallbacks: rows.filter(r => !r.unpriced && r.areaBasis === 'survey').length,
    },
  };
}

// ---------- opportunities ----------
export interface ShortlistBrand { id: string; name: string; category: BrandCategory | null; fitScore: number; surfaceMin?: number | null; surfaceMax?: number | null; logoUrl?: string | null }
/** Whether a brand's usual store size suits a shop: within its stated range, with a quarter of slack either side; unknown when the brand states none. */
export function sizeFit(brand: ShortlistBrand, areaSqm: number): 'fits' | 'unknown' | 'no' {
  const lo = brand.surfaceMin ?? brand.surfaceMax ?? null, hi = brand.surfaceMax ?? brand.surfaceMin ?? null;
  if (lo === null || hi === null || areaSqm <= 0) return 'unknown';
  return areaSqm >= lo * 0.75 && areaSqm <= hi * 1.25 ? 'fits' : 'no';
}
export interface Opportunity {
  shopId: string;
  kind: 'vacant' | 'weak' | 'expiring';
  evidence: string;
  /** Potential rent minus today's, per year; null when it cannot be priced. */
  value: number | null;
  brands: ShortlistBrand[];
}
const HIGH = 75;
export function opportunities(places: PulsePlace[], roll: RentRollRow[], scenario: PulseScenario, shortlist: ShortlistBrand[], now = today()): Opportunity[] {
  const let_ = places.filter(p => p.category !== 'VACANT');
  const density = (p: PulsePlace) => p.shop?.reviews != null && p.feature.areaSqm > 0 ? p.shop.reviews / p.feature.areaSqm : null;
  const densities = let_.map(density).filter((d): d is number => d !== null).sort((a, b) => a - b);
  const median = densities.length ? densities[Math.floor(densities.length / 2)]! : null;
  const present = new Set(let_.map(p => p.category));
  const tenantIds = new Set(let_.flatMap(p => p.brandId ? [p.brandId] : []));
  const tenants = new Set(let_.map(p => normalise(p.name ?? '')));
  const used = new Set(scenario.moves.map(m => m.brandId));
  const horizon = new Date(now); horizon.setMonth(horizon.getMonth() + 24);
  const until = horizon.toISOString().slice(0, 10);
  // A brand whose usual store size suits the shop comes first, then Source's fit score, one per trade so the three are
  // real alternatives; a brand too big or too small for the shop, or of the outgoing tenant's own trade, is left out.
  const suggest = (place: PulsePlace) => {
    const picks: ShortlistBrand[] = [];
    const rank = (b: ShortlistBrand) => sizeFit(b, place.feature.areaSqm) === 'fits' ? 0 : 1;
    for (const b of shortlist.filter(b => !used.has(b.id) && !tenantIds.has(b.id) && !tenants.has(normalise(b.name)) && b.category && b.category !== place.category && sizeFit(b, place.feature.areaSqm) !== 'no')
      .sort((a, b) => rank(a) - rank(b) || b.fitScore - a.fitScore || Number(present.has(a.category!)) - Number(present.has(b.category!)))) {
      if (!picks.some(p => p.category === b.category)) picks.push(b);
      if (picks.length === 3) break;
    }
    return picks;
  };
  const value = (place: PulsePlace, current: number | null) => scenario.referenceRent && place.feature.exposure && current !== null
    ? Math.round(potentialRent(scenario.referenceRent, place.feature.exposure.percentile) * place.feature.areaSqm - current) : null;
  const list: Opportunity[] = [];
  for (const place of places) {
    const p = place.feature.exposure?.percentile;
    if (p === undefined || p < HIGH || scenario.moves.some(m => m.shopId === place.feature.id)) continue;
    const row = rentRowForPlace(place, roll);
    const busy = `${ordinal(p)} percentile of passing traffic (threshold: ${ordinal(HIGH)})`;
    if (place.category === 'VACANT') list.push({ shopId: place.feature.id, kind: 'vacant', evidence: `Empty shop in the ${busy}.`, value: value(place, 0), brands: suggest(place) });
    else if (median !== null && density(place) !== null && density(place)! < median) list.push({ shopId: place.feature.id, kind: 'weak', evidence: `${place.name} draws ${place.shop!.reviews!.toLocaleString('en-GB')} Google review${place.shop!.reviews === 1 ? '' : 's'}${place.shop!.reviews === 0 ? ' (the listing may simply be missing)' : ''} on ${place.feature.areaSqm.toLocaleString('en-GB')} m² (${density(place)!.toFixed(2)} per m², centre median ${median.toFixed(2)}), in the ${busy}.`, value: value(place, row?.annualRent ?? null), brands: suggest(place) });
    else if (row?.leaseEnd && row.leaseEnd >= now && row.leaseEnd <= until) list.push({ shopId: place.feature.id, kind: 'expiring', evidence: `${place.name}'s lease ends ${row.leaseEnd} (within 24 months), in the ${busy}.`, value: value(place, row.annualRent), brands: suggest(place) });
  }
  return list.sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity) || (byPercentile(places, b) - byPercentile(places, a)));
}
const byPercentile = (places: PulsePlace[], o: Opportunity) => places.find(p => p.feature.id === o.shopId)?.feature.exposure?.percentile ?? 0;

// ---------- the Business Plan sheet ----------
const col = (c: number) => String.fromCharCode(65 + c);
/** The scenario as a sheet with live formulas: assumptions on top, one row per priced move, value created at the bottom. */
export function businessPlanSheet(propertyId: string, scenario: PulseScenario, rows: PricedMove[], levelName: (id: string | null) => string): TableSheet {
  const cells: Record<string, string> = {};
  const put = (r: number, c: number, v: string | number | null) => { if (v !== null && v !== '') cells[`${r},${c}`] = String(v); };
  put(0, 0, 'Assumption'); put(0, 1, 'Value');
  put(1, 0, 'Reference rent (€/m²/year)'); put(1, 1, scenario.referenceRent);
  put(2, 0, 'Yield (%)'); put(2, 1, scenario.yieldPct);
  const head = ['Shop', 'Level', 'Today', 'Move', 'New tenant', 'Category', 'Start', 'Area (m²)', 'Area basis', 'Current rent (€/yr)', 'Target (€/m²/yr)', 'Target rent (€/yr)', 'Uplift (€/yr)', 'Rent-free months', 'Rent-free cost (€)', 'Works (€)'];
  const top = 5;
  head.forEach((h, c) => put(top, c, h));
  const priced = rows.filter(r => !r.unpriced);
  priced.forEach((r, i) => {
    const row = top + 1 + i, n = row + 1;
    [r.move.shopId, levelName(r.place?.feature.level ?? null), r.place?.name ?? 'Vacant', r.move.type === 'relet' ? 'Re-let' : 'Vacate', r.move.brandName, r.move.brandCategory, r.move.start, r.areaSqm, r.areaBasis, r.currentRent, r.move.type === 'relet' ? r.move.rentPerSqm : 0]
      .forEach((v, c) => put(row, c, v as string | number | null));
    put(row, 11, `=H${n}*K${n}`);
    put(row, 12, `=L${n}-J${n}`);
    put(row, 13, r.move.rentFreeMonths);
    put(row, 14, `=N${n}*L${n}/12`);
    put(row, 15, r.move.works);
  });
  const first = top + 2, last = top + 1 + priced.length;
  put(3, 0, 'Value created (€)');
  put(3, 1, priced.length ? `=SUM(${col(12)}${first}:${col(12)}${last})/(B3/100)-SUM(${col(15)}${first}:${col(15)}${last})-SUM(${col(14)}${first}:${col(14)}${last})` : 0);
  const styles = Object.fromEntries([[`0,0`, 0], [`0,1`, 0], ...head.map((_, c) => [`${top},${c}`, 0])].map(([k]) => [k, { bold: true, background: '#1B1B1B', color: '#ECECEC' }]));
  return { id: businessPlanSheetId(propertyId), name: BUSINESS_PLAN_NAME, cells, styles };
}

/** The workbook's sheets with the Business Plan written in place; null when a user sheet already owns the name and the caller has not confirmed. */
export function withBusinessPlan(workbook: TableDoc, sheet: TableSheet, confirmOverwrite: boolean): TableSheet[] | null {
  const clash = workbook.sheets.find(s => s.name.toLowerCase() === sheet.name.toLowerCase() && s.id !== sheet.id);
  if (clash && !confirmOverwrite) return null;
  const others = workbook.sheets.filter(s => s.id !== sheet.id && s !== clash);
  return [...others, sheet];
}
